import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PlaceDetails } from 'GooglePlaces';
import { Modal } from 'Modal';
import { FilterControls } from './FilterControls';
import { ScrollFadeContainer } from '../ScrollFadeContainer';
import { type FilterState, type FilterTab } from '../../hooks/usePeopleFilters.types';
import { hasActiveFiltersInState } from '../../hooks/usePeopleFilters.utils';
import { INITIAL_FILTER_STATE } from '../../peopleFiltersSlice';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type DesktopFiltersModalProps = {
    filterState: FilterState;
    onApply: (state: FilterState) => void;
    onClose: () => void;
};

// oxlint-disable-next-line max-lines-per-function -- desktop filters modal with draft buffering
export function DesktopFiltersModal({ filterState, onApply, onClose }: DesktopFiltersModalProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.filters' });

    const [draft, setDraft] = useState<FilterState>(() => ({ ...filterState }));

    const setDraftField = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    }, []);

    const hasActiveFilters = useMemo(() => hasActiveFiltersInState(draft), [draft]);

    const resetAndApply = useCallback(() => {
        const reset = { ...INITIAL_FILTER_STATE, showAdvanced: draft.showAdvanced };
        onApply(reset);
        onClose();
    }, [draft.showAdvanced, onApply, onClose]);

    const handleApply = useCallback(() => {
        onApply(draft);
        onClose();
    }, [draft, onApply, onClose]);

    return (
        <Modal title={t('filtersTitle')} onClose={onClose} size="lg" titleAlignment="start">
            <ScrollFadeContainer className="max-h-[60vh] overflow-y-auto px-7.5 pb-2">
                <FilterControls
                    keywordSearch={draft.keywordSearch}
                    onKeywordSearchChange={(v: string) => setDraftField('keywordSearch', v)}
                    activeTab={draft.activeTab}
                    onTabChange={(v: FilterTab) => setDraftField('activeTab', v)}
                    showAdvanced={draft.showAdvanced}
                    onShowAdvancedChange={(v: boolean) => setDraftField('showAdvanced', v)}
                    places={draft.places}
                    onPlacesChange={(v: PlaceDetails[]) => setDraftField('places', v)}
                    programTypes={draft.programTypes}
                    onProgramTypesChange={(v: string[]) => setDraftField('programTypes', v)}
                    companies={draft.companies}
                    onCompaniesChange={(v: string[]) => setDraftField('companies', v)}
                    industries={draft.industries}
                    onIndustriesChange={(v: string[]) => setDraftField('industries', v)}
                    interests={draft.interests}
                    onInterestsChange={(v: string[]) => setDraftField('interests', v)}
                    lookingFor={draft.lookingFor}
                    onLookingForChange={(v: string[]) => setDraftField('lookingFor', v)}
                    timezones={draft.timezones}
                    onTimezonesChange={(v: string[]) => setDraftField('timezones', v)}
                    hideSearch
                    hasActiveFilters={hasActiveFilters}
                    onReset={resetAndApply}
                />
            </ScrollFadeContainer>

            <div className="px-7.5 pb-7.5 pt-4">
                <button
                    type="button"
                    onClick={handleApply}
                    className="h-12 w-full rounded-[12px] bg-black text-base font-medium text-white"
                >
                    {t('applyFilters')}
                </button>
            </div>
        </Modal>
    );
}
